<template>
  <div class="template-editor-column">
    <div class="editor-canvas" @click="handleCanvasClick">
      <draggable
        v-model="templateRootParts"
        :group="{ name: 'form-parts', put: canAcceptRootPart }"
        draggable=".draggable-item"
        handle=".handle"
        :animation="150"
        easing="cubic-bezier(1, 0, 0, 1)"
        class="root-parts-list"
        data-section-id="root"
        :move="onDragMove"
        @add="onAdd"
        @end="onDragEnd"
      >
        <FormTemplatePart
          v-for="(partId, index) in templateRootParts"
          :key="typeof partId === 'object' ? 'temp-' + index : partId"
          :partId="partId"
          class="draggable-item"
          @add="onAdd"
        />
        <div slot="footer" class="canvas-footer" v-if="template.id">
          <!-- The footer can be used to display an empty state or quick add buttons at the bottom -->
          <div class="empty-state" v-if="templateRootParts.length === 0">
            Drag components from the palette here to start building your form.
          </div>
        </div>
      </draggable>
    </div>
    <ReusableFieldSelector
      v-if="showReusableSelector"
      @cancel="cancelReusableDrop"
      @save="saveReusableDrop"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex'
import Draggable from 'vuedraggable'
import FormTemplatePart from '../FormTemplatePart'
import ReusableFieldSelector from '../ReusableFieldSelector'

export default {
  name: 'TemplateEditor',
  components: {
    draggable: Draggable,
    FormTemplatePart,
    ReusableFieldSelector
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      draggedElement: null,
      showReusableSelector: false,
      reusableFieldDropEvent: null
    }
  },
  computed: {
    ...mapGetters('formtemplate', [
      'template',
      'partById'
    ]),
    ...mapState('formtemplate', [
      'viewArchived',
      'viewIdentifiers',
      'activePartId'
    ]),
    // We need a computed setter to work with vuedraggable v-model
    templateRootParts: {
      get() {
        return this.template.root_parts || []
      },
      set(value) {
        // Vuex mutation should ideally handle this, but currently vuedraggable operates directly on it
        // Depending on existing implementation, we might need a specific action/mutation.
        // For now, we update the object reactively.
        this.$store.commit('formtemplate/setTemplate', {
          ...this.template,
          root_parts: value
        })
      }
    }
  },
  methods: {
    ...mapMutations('formtemplate', []),
    ...mapActions('formtemplate', [
      'moveSection',
      'moveParagraph',
      'movePagebreak',
      // For palette drops:
      'saveSection',
      'saveParagraph',
      'saveQuestion',
      'savePagebreak',
      'saveEsignature',
      'saveTemplate'
    ]),
    setViewArchived(val) {
      this.$store.state.formtemplate.viewArchived = val
    },
    toggleViewIdentifiers() {
      this.$store.state.formtemplate.viewIdentifiers = !this.$store.state.formtemplate.viewIdentifiers
    },
    handleCanvasClick(e) {
      // If clicking on the empty canvas background, deselect
      if (e.target.classList.contains('editor-canvas') || e.target.classList.contains('root-parts-list')) {
        this.$store.commit('formtemplate/setActivePartId', null)
      }
    },
    canAcceptRootPart(to, from, dragEl) {
      if (dragEl.dataset && dragEl.dataset.partType) {
        const type = dragEl.dataset.partType
        return type === 's' || type === 'p' || type === 'b'
      }

      const vueInstance = dragEl.__vue__
      if (!vueInstance) return true
      const partId = vueInstance.partId || (vueInstance.$parent && vueInstance.$parent.partId)
      if (!partId) return true
      const part = this.partById(partId)
      return !part || part.part_type === 's' || part.part_type === 'p' || part.part_type === 'b'
    },
    onDragMove(e) {
      this.draggedElement = e.draggedContext.element
    },
    onDragEnd(event) {
      if (!this.draggedElement) return
      let movedPart = this.partById(this.draggedElement)
      this.draggedElement = null
      if (!movedPart) return

      const targetEl = event.to
      const sectionEl = targetEl.closest('[data-section-id]')
      const targetSectionId = sectionEl ? sectionEl.dataset.sectionId : undefined
      const toSection = (!targetSectionId || targetSectionId === 'root')
        ? null
        : parseInt(targetSectionId)

      let insert_before = null
      // Get the parts array representing the target list
      const targetList = toSection ? this.partById(toSection).parts : this.templateRootParts
      // event.newIndex is the position of the dropped item in the visual array.
      // The item has already been inserted into the target array by VueDraggable,
      // so the element currently at newIndex + 1 is the item that will be natively AFTER it.
      if (targetList && targetList.length > event.newIndex + 1) {
          const nextItem = targetList[event.newIndex + 1]
          insert_before = typeof nextItem === 'object' ? nextItem.id : nextItem || null
      }

      if (movedPart.part_type === 's') {
        this.moveSection({ section: movedPart, insert_before, toSection })
      } else if (movedPart.part_type === 'p') {
        this.moveParagraph({ paragraph: movedPart, insert_before, toSection })
      } else if (movedPart.part_type === 'b') {
        this.movePagebreak({ pagebreak: movedPart, insert_before, toSection })
      }
    },
    async onAdd(event) {
      // Triggered when an element from another list (ComponentPalette) is dropped here.
      const element = event.item._underlying_vm_
      if (!element || !element.isNew) return

      const targetEl = event.to
      const sectionEl = targetEl.closest('[data-section-id]')
      const targetSectionId = sectionEl && sectionEl.dataset.sectionId !== 'root' 
        ? parseInt(sectionEl.dataset.sectionId) 
        : null

      if (element.part_type === 'reusable') {
        this.reusableFieldDropEvent = event
        this.showReusableSelector = true
        return
      }

      let insert_before = null
      const targetList = targetSectionId ? this.partById(targetSectionId).parts : this.templateRootParts
      if (targetList && targetList.length > event.newIndex + 1) {
          const nextItem = targetList[event.newIndex + 1]
          insert_before = typeof nextItem === 'object' ? nextItem.id : nextItem || null
      }

      // Create new part payload
      const payload = {
        template: this.template.id,
        part_type: element.part_type,
        section: targetSectionId,
        insert_before: insert_before
      }

      if (element.part_type === 'q') {
        payload.is_list = element.is_list || false
        payload.default_rows = 1
        payload.fieldtype = {
          atomic_type: element.atomic_type
        } // The API or FormTemplateQuestion will handle full creation later
      }

      this.executeDropSave(element.part_type, payload, targetSectionId)
    },
    async executeDropSave(partType, payload, targetSectionId) {
      try {
        let savedPart;
        switch (partType) {
          case 's':
            savedPart = await this.saveSection(payload)
            break
          case 'p':
            savedPart = await this.saveParagraph(payload)
            break
          case 'q':
            savedPart = await this.saveQuestion(payload)
            break
          case 'b':
            savedPart = await this.savePagebreak(payload)
            break
          case 'e':
            savedPart = await this.saveEsignature(payload)
            break
        }
        
        // Remove the temporary cloned object from the list and replace with partId
        if (targetSectionId) {
            // Update the section's parts array.
            const section = this.partById(targetSectionId)
            if (section && section.parts) {
              const tempIndex = section.parts.findIndex(p => p && p.isNew)
              if (tempIndex !== -1) {
                section.parts.splice(tempIndex, 1, savedPart.id)
              }
            }
        } else {
            // For root parts
            const newRootParts = [...this.template.root_parts]
            const tempIndex = newRootParts.findIndex(p => p && p.isNew)
            if (tempIndex !== -1) {
              newRootParts.splice(tempIndex, 1, savedPart.id)
              this.templateRootParts = newRootParts
            }
        }

        // Select the newly created part automatically
        this.$store.commit('formtemplate/setActivePartId', savedPart.id)
        
      } catch (err) {
        // console.error("Failed to add new component from palette", err)
        // Revert UI insert if failed
        if (targetSectionId) {
            const section = this.partById(targetSectionId)
            if (section && section.parts) {
               const newParts = section.parts.filter(p => p && !p.isNew)
               section.parts.splice(0, section.parts.length, ...newParts)
            }
        } else {
            const newRootParts = this.template.root_parts.filter(p => p && !p.isNew)
            this.templateRootParts = newRootParts
        }
      }
    },
    cancelReusableDrop() {
      this.showReusableSelector = false
      if (!this.reusableFieldDropEvent) return

      const targetEl = this.reusableFieldDropEvent.to
      const sectionEl = targetEl.closest('[data-section-id]')
      const targetSectionId = sectionEl && sectionEl.dataset.sectionId !== 'root' 
        ? parseInt(sectionEl.dataset.sectionId) 
        : null
      
      if (targetSectionId) {
          const section = this.partById(targetSectionId)
          if (section && section.parts) {
             const newParts = section.parts.filter(p => !p.isNew)
             section.parts.splice(0, section.parts.length, ...newParts)
          }
      } else {
          const newRootParts = this.template.root_parts.filter(p => !p.isNew)
          this.templateRootParts = newRootParts
      }
      this.reusableFieldDropEvent = null
    },
    saveReusableDrop(fieldTypeId) {
      this.showReusableSelector = false
      if (!this.reusableFieldDropEvent) return

      const event = this.reusableFieldDropEvent
      this.reusableFieldDropEvent = null
      
      const targetEl = event.to
      const sectionEl = targetEl.closest('[data-section-id]')
      const targetSectionId = sectionEl && sectionEl.dataset.sectionId !== 'root' 
        ? parseInt(sectionEl.dataset.sectionId) 
        : null

      let insert_before = null
      const targetList = targetSectionId ? this.partById(targetSectionId).parts : this.templateRootParts
      if (targetList && targetList.length > event.newIndex + 1) {
          const nextItem = targetList[event.newIndex + 1]
          insert_before = typeof nextItem === 'object' ? nextItem.id : nextItem || null
      }

      const payload = {
        template: this.template.id,
        part_type: 'q',
        section: targetSectionId,
        insert_before: insert_before,
        is_list: false,
        default_rows: 1,
        fieldtype_id: fieldTypeId
      }

      this.executeDropSave('q', payload, targetSectionId)
    }
  }
}
</script>

<style scoped>
.template-editor-column {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f5f5f5;
}

.editor-header {
  background-color: #fff;
  border-bottom: 1px solid #e0e0e0;
  padding: 16px;
  min-height: 64px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-area h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.editor-canvas {
  flex-grow: 1;
  overflow-y: auto;
  padding: 24px;
  position: relative;
}

.root-parts-list {
  min-height: 100%;
}

.draggable-item {
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.draggable-item.is-active {
  border-left: 4px solid #2196f3;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background-color: #fff !important;
  transform: translateX(2px);
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
  border: 2px dashed #ccc;
  border-radius: 8px;
  margin-top: 20px;
}
</style>
